import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, ShieldCheck, Palette, Zap, Coins, Trophy, BarChart3, Gift, Shirt, Globe, Sparkles, Loader2 } from "lucide-react";

const PERKS = [
  { icon: ShieldCheck, text: "Green verified checkmark on your profile" },
  { icon: Trophy, text: "Profile boost — get seen more in search" },
  { icon: Crown, text: "🌟 Elite profile badge" },
  { icon: Palette, text: "Exclusive futuristic themes & avatar frames" },
  { icon: Zap, text: "Double XP during special events" },
  { icon: Coins, text: "Higher daily coin rewards + monthly bonus coins" },
  { icon: Trophy, text: "Exclusive weekly cleanup challenges" },
  { icon: BarChart3, text: "Advanced stats & lifetime impact tracking" },
  { icon: Shirt, text: "Exclusive equipment skins (gloves, grabbers, backpacks)" },
  { icon: Globe, text: "Early access to new cities, maps & features" },
  { icon: Sparkles, text: "Exclusive seasonal events & competitions" },
];

export default function Elite() {
  const { user, applyUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    setBusy(true);
    try { const { data } = await api.post("/elite/subscribe"); applyUser(data.user); toast.success("Welcome to Jixxeo Elite! 👑"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { const { data } = await api.post("/elite/cancel"); applyUser(data.user); toast.success("Elite membership cancelled"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl p-6 elite-shimmer text-white neon-purple text-center">
        <Crown className="w-14 h-14 mx-auto pulse-glow" />
        <h1 className="font-display text-3xl mt-3">JIXXEO ELITE</h1>
        <p className="text-white/90 mt-1 text-sm">Support development. Unlock the ultimate hunter experience.</p>
        <p className="font-display text-4xl mt-4">$10.99<span className="text-base font-body">/mo</span></p>
      </div>

      {user?.elite && (
        <div className="glass rounded-2xl p-4 border border-jixxeo-green/40 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-jixxeo-green" />
          <div className="flex-1"><p className="font-bold text-jixxeo-green">You're an Elite member</p><p className="text-xs text-muted-foreground">All perks active. Thank you for supporting Jixxeo!</p></div>
        </div>
      )}

      <div className="glass rounded-3xl p-5">
        <h3 className="font-head font-extrabold text-lg mb-4">Everything included</h3>
        <div className="space-y-3">
          {PERKS.map((p, i) => { const I = p.icon; return (
            <div key={i} className="flex items-center gap-3" data-testid={`perk-${i}`}>
              <div className="w-8 h-8 rounded-full bg-jixxeo-purple/20 flex items-center justify-center shrink-0"><I className="w-4 h-4 text-jixxeo-purple" /></div>
              <p className="text-sm text-white/85">{p.text}</p>
              <Check className="w-4 h-4 text-jixxeo-green ml-auto shrink-0" />
            </div>
          ); })}
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">Billing is simulated in this build — toggling Elite applies perks instantly.</p>

      {user?.elite ? (
        <Button onClick={cancel} disabled={busy} data-testid="cancel-elite-btn" variant="ghost" className="w-full h-12 rounded-full glass border border-white/10 text-jixxeo-danger font-bold">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Cancel Elite"}</Button>
      ) : (
        <Button onClick={subscribe} disabled={busy} data-testid="subscribe-elite-btn" className="w-full h-14 rounded-full elite-shimmer text-white font-display text-lg">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Crown className="w-5 h-5 mr-2" />Become Elite</>}</Button>
      )}
    </div>
  );
}
